import { useState, useEffect, useMemo } from 'react';
import type { CSSProperties } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import MemberCard from './MemberCard';
import ProfileModal from './ProfileModal';
import { getCountryMembers } from '../utils/api';
import { useClickSound } from '../hooks/useClickSound';
import { getFlagImageUrl } from '../utils/flags';
import type { Country, Member, User } from '../types';

// Flag image component with fallback - rectangular to show full flag respectfully
function FlagImage({ code, size = 32, style }: { code: string; size?: number; style?: CSSProperties }) {
  const [error, setError] = useState(false);
  const flagWidth = size * 1.5; // 3:2 aspect ratio
  const flagHeight = size;
  
  if (error) {
    return (
      <span style={{ fontSize: size * 0.5, fontWeight: 600, color: 'white', ...style }}>
        {code}
      </span>
    );
  }
  
  return (
    <img 
      src={getFlagImageUrl(code)} 
      alt={code}
      onError={() => setError(true)}
      style={{ 
        width: flagWidth, 
        height: flagHeight, 
        objectFit: 'cover', 
        borderRadius: '4px',
        ...style 
      }}
    />
  );
}

interface CountryPanelProps {
  country: Country | null;
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  isAuthenticated: boolean;
  onJoin: (countryCode: string) => Promise<void>;
  onLeave: () => Promise<void>;
  joinLoading: boolean;
}

// Heat signature colors based on member count
function getHeatColor(memberCount: number): string {
  if (memberCount <= 5) return '#14F195';   // Green - few
  if (memberCount <= 20) return '#FFC107';  // Yellow
  if (memberCount <= 50) return '#FF9800';  // Orange
  return '#EF4444';                          // Red - many
}

function getHeatLabel(memberCount: number): string {
  if (memberCount <= 5) return 'Growing';
  if (memberCount <= 20) return 'Active';
  if (memberCount <= 50) return 'Popular';
  return 'Thriving';
}

export default function CountryPanel({ 
  country, 
  isOpen, 
  onClose,
  user,
  isAuthenticated,
  onJoin,
  onLeave,
  joinLoading,
}: CountryPanelProps) {
  const [isMobile, setIsMobile] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const queryClient = useQueryClient();
  const { withClickSound } = useClickSound();

  // Check if user is representing this country
  const isRepresenting = user?.countryCode === country?.code;

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Refresh members when user joins/leaves
  useEffect(() => {
    if (country && isOpen) {
      queryClient.invalidateQueries({ queryKey: ['countryMembers', country.code] });
    }
  }, [user?.countryCode, country, isOpen, queryClient]);

  const handleJoinClick = async () => {
    if (country) {
      await onJoin(country.code);
    }
  };

  const handleLeaveClick = async () => {
    await onLeave();
  };

  const { data: membersData, isLoading } = useQuery({
    queryKey: ['countryMembers', country?.code],
    queryFn: () => country ? getCountryMembers(country.code) : Promise.resolve({ members: [], totalCount: 0 }),
    enabled: !!country && isOpen,
  });

  const members = useMemo(() => {
    return membersData?.members || [];
  }, [membersData]);

  if (!country) return null;

  const heatColor = getHeatColor(country.memberCount);
  const heatLabel = getHeatLabel(country.memberCount);

  const backdropStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(4px)',
    zIndex: isMobile ? 200 : 40,
    transition: 'opacity 0.3s',
    opacity: isOpen ? 1 : 0,
    pointerEvents: isOpen ? 'auto' : 'none',
  };

  const panelStyle: CSSProperties = {
    position: 'fixed',
    zIndex: isMobile ? 201 : 50,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: 'rgba(10, 10, 20, 0.95)',
    backdropFilter: 'blur(30px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
    ...(isMobile ? {
      bottom: 0,
      left: 0,
      right: 0,
      borderRadius: '20px 20px 0 0',
      maxHeight: '85vh',
      transform: isOpen ? 'translateY(0)' : 'translateY(100%)',
    } : {
      top: '70px',
      right: '16px',
      bottom: '70px',
      width: '380px',
      borderRadius: '20px',
      transform: isOpen ? 'translateX(0)' : 'translateX(calc(100% + 32px))',
    }),
  };

  const headerStyle: CSSProperties = {
    flexShrink: 0,
    padding: '20px',
    position: 'relative',
  };

  const statCardStyle: CSSProperties = {
    flex: 1,
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.05)',
    borderRadius: '12px',
    padding: '12px',
    textAlign: 'center',
  };

  return (
    <>
      <div style={backdropStyle} onClick={onClose} />

      <div style={panelStyle}>
        {/* Header */}
        <div style={headerStyle}>
          {isMobile && (
            <div style={{ width: '40px', height: '4px', background: 'rgba(255,255,255,0.2)', borderRadius: '2px', margin: '0 auto 16px' }} />
          )}
          
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {/* Flag emoji in circle */}
              <div style={{ 
                width: '56px', 
                height: '56px', 
                borderRadius: '50%', 
                background: `linear-gradient(135deg, ${heatColor}22, ${heatColor}44)`,
                border: `2px solid ${heatColor}`,
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                boxShadow: `0 0 20px ${heatColor}44`,
                overflow: 'hidden',
              }}>
                <FlagImage code={country.code} size={52} />
              </div>
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: 700, color: 'white' }}>{country.name}</h2>
                <p style={{ color: heatColor, fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>
                  {heatLabel} Community
                </p>
              </div>
            </div>
            
            <button
              onClick={withClickSound(onClose)}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '10px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.5)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
              }}
            >
              ×
            </button>
          </div>

          {/* Stats */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
            <div style={statCardStyle}>
              <div style={{ fontSize: '20px', fontWeight: 700, color: heatColor }}>{country.memberCount}</div>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '1px' }}>Representing</div>
            </div>
            <div style={statCardStyle}>
              <div style={{ 
                width: '20px', 
                height: '20px', 
                borderRadius: '50%', 
                background: heatColor, 
                margin: '0 auto 4px',
                boxShadow: `0 0 10px ${heatColor}`,
              }} />
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '1px' }}>Heat</div>
            </div>
            <div style={statCardStyle}>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <FlagImage code={country.code} size={28} />
              </div>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '1px' }}>Flag</div>
            </div>
          </div>

          {/* No Superteam Notice */}
          <div style={{
            marginTop: '16px',
            padding: '12px',
            background: 'rgba(153, 69, 255, 0.1)',
            border: '1px solid rgba(153, 69, 255, 0.2)',
            borderRadius: '12px',
          }}>
            <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>
              <span style={{ color: '#9945FF', fontWeight: 600 }}>💡 No Superteam yet!</span>{' '}
              Join to represent your country and help grow the Solana community here. 
              More members = more visibility for a potential Superteam!
            </p>
          </div>
        </div>
        
        {/* Divider */}
        <div style={{ height: '1px', background: `linear-gradient(90deg, transparent, ${heatColor}66, transparent)`, margin: '0 20px' }} />

        {/* Members List */}
        <div className="custom-scrollbar" style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'white' }}>Country Representatives</h3>
            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.05)', padding: '4px 8px', borderRadius: '6px' }}>
              {members.length} showing
            </span>
          </div>

          {isLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '64px 0' }}>
              <div className="animate-spin" style={{ width: '32px', height: '32px', border: `2px solid ${heatColor}44`, borderTopColor: heatColor, borderRadius: '50%' }} />
            </div>
          ) : members.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {members.map((member) => (
                <MemberCard 
                  key={member.id} 
                  member={member}
                  canVerify={false}
                  verifyLoading={false}
                  onClick={setSelectedMember}
                />
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '64px 0' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: `${heatColor}22`, border: `1px solid ${heatColor}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', overflow: 'hidden' }}>
                <FlagImage code={country.code} size={60} />
              </div>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', fontWeight: 500 }}>No representatives yet</p>
              <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px', marginTop: '4px' }}>Be the first to represent {country.name}!</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ flexShrink: 0, padding: '20px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          {isRepresenting ? (
            // User is already representing this country - show Leave button
            <button 
              onClick={withClickSound(handleLeaveClick)}
              disabled={joinLoading}
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: '14px',
                fontWeight: 600,
                fontSize: '14px',
                background: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#ef4444',
                cursor: joinLoading ? 'not-allowed' : 'pointer',
                opacity: joinLoading ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              {joinLoading ? (
                <div style={{ width: '18px', height: '18px', border: '2px solid rgba(239,68,68,0.3)', borderTopColor: '#ef4444', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              ) : null}
              Stop Representing {country.name}
            </button>
          ) : isAuthenticated ? (
            // User is logged in but not representing - show Join button
            <button 
              onClick={withClickSound(handleJoinClick)}
              disabled={joinLoading}
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: '14px',
                fontWeight: 600,
                fontSize: '14px',
                background: `linear-gradient(to right, ${heatColor}, ${heatColor}cc)`,
                color: heatColor === '#14F195' || heatColor === '#FFC107' ? 'black' : 'white',
                border: 'none',
                cursor: joinLoading ? 'not-allowed' : 'pointer',
                opacity: joinLoading ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              {joinLoading ? (
                <div style={{ width: '18px', height: '18px', border: '2px solid rgba(0,0,0,0.3)', borderTopColor: 'black', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              ) : (
                <FlagImage code={country.code} size={18} />
              )}
              Represent {country.name}
            </button>
          ) : (
            // User is not logged in - show Sign In button
            <button 
              onClick={withClickSound(handleJoinClick)}
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: '14px',
                fontWeight: 600,
                fontSize: '14px',
                background: `linear-gradient(to right, ${heatColor}, ${heatColor}cc)`,
                color: heatColor === '#14F195' || heatColor === '#FFC107' ? 'black' : 'white',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <svg style={{ width: '18px', height: '18px' }} viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              Sign in to Represent
            </button>
          )}
        </div>
      </div>

      {/* Profile Modal */}
      {selectedMember && (
        <ProfileModal 
          member={selectedMember} 
          onClose={() => setSelectedMember(null)} 
        />
      )}
    </>
  );
}
